import React, { useCallback, useEffect, useState } from "react";
import { Link2Off, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";

export function MicrosoftLinkCard({ userId, isSelf }) {
  const [link, setLink] = useState(null);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const base = `/admin/users/${userId}`;

  const load = useCallback(async () => {
    try { setLink((await api.get(`${base}/microsoft-link`)).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const run = async (call, success) => {
    setBusy(true);
    try { setLink((await call()).data); toast.success(success); setAddress(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const add = () => run(() => api.post(`${base}/microsoft-usernames`, { username: address.trim() }), "Microsoft username added");
  const unlink = () => {
    if (!window.confirm("Unlink this Microsoft account? Added usernames are removed and the next Microsoft sign-in has to match again.")) return;
    run(() => api.delete(`${base}/microsoft-link`), "Microsoft account unlinked");
  };

  if (!link) return null;
  const canUnlink = link.linked || link.usernames.length > 0;
  return (
    <section className="border p-4 space-y-3" style={{ borderColor: "var(--border)" }} data-testid="microsoft-link-card">
      <div className="eyebrow">Microsoft 365 sign-in</div>
      <div className="text-sm" style={{ color: "var(--ink)" }} data-testid="microsoft-link-status">
        {link.linked
          ? <>Linked to <span className="font-mono">{link.linked_username || "Microsoft account"}</span></>
          : "Not linked"}
      </div>
      {link.linked && (
        <div className="text-xs" style={{ color: "var(--muted)" }} data-testid="microsoft-link-dates">
          {link.linked_at ? `Linked ${formatWhen(link.linked_at)} · ` : ""}last Microsoft sign-in {formatWhen(link.last_sign_in_at)}
        </div>
      )}
      {link.usernames.length > 0 && (
        <div className="text-xs" style={{ color: "var(--muted)" }} data-testid="microsoft-usernames-list">
          Added usernames: <span className="font-mono">{link.usernames.join(", ")}</span>
        </div>
      )}
      {isSelf ? (
        <p className="text-xs" style={{ color: "var(--muted)" }} data-testid="microsoft-link-self-note">
          Another administrator must change the Microsoft link on your own account.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <input className="input-flat flex-1 min-w-[200px]" type="email" placeholder="e.g. name@mye1.us"
                 value={address} onChange={(e) => setAddress(e.target.value)} data-testid="microsoft-username-input" />
          <button type="button" className="btn-ghost text-xs flex items-center gap-1" disabled={busy || !address.trim()}
                  onClick={add} data-testid="microsoft-username-add"><Plus size={12} /> Add Microsoft username</button>
          {canUnlink && (
            <button type="button" className="btn-ghost text-xs flex items-center gap-1" disabled={busy}
                    onClick={unlink} data-testid="microsoft-unlink"><Link2Off size={12} /> Unlink</button>
          )}
        </div>
      )}
    </section>
  );
}
